import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, X, ArrowLeft, User } from "lucide-react";
import { normalizeSexToA, normalizeSexToB, isSexConstraintError } from "@/lib/normalize";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

type Patient = {
  id?: string;
  full_name: string;
  cpf: string;
  email?: string;
  celular?: string;
  data_nascimento?: string;
  sexo?: string;
  avatar_url?: string;
};

export default function PacienteEditar() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPatientPortal = pathname.startsWith("/paciente/");
  const basePath: "/app" | "/paciente" = isPatientPortal ? "/paciente" : "/app";
  const patientDetailsPath = id ? `${basePath}/pacientes/${id}` : `${basePath}/pacientes`;
  const patientsListPath = isPatientPortal ? "/paciente/minha-saude" : "/app/pacientes";
  const [form, setForm] = useState<Patient>({
    full_name: "",
    cpf: "",
    email: "",
    celular: "",
    data_nascimento: "",
    sexo: "",
    avatar_url: ""
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadPatient();
    }
  }, [id]);

  const loadPatient = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      if (data) {
        setForm({
          full_name: data.full_name || "",
          cpf: data.cpf || "",
          email: data.email || "",
          celular: data.celular || "",
          data_nascimento: data.data_nascimento || "",
          sexo: data.sexo || "",
          avatar_url: data.avatar_url || ""
        });
        setAvatarPreview(data.avatar_url || null);
      }
    } catch (error: any) {
      console.error("Error loading patient:", error);
      toast.error("Erro ao carregar paciente");
      navigate(patientsListPath);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagem deve ter no máximo 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Arquivo deve ser uma imagem");
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setForm(f => ({ ...f, avatar_url: "" }));
  };

  async function handleSave() {
    try {
      setBusy(true);
      setErr(null);

      if (!form.full_name.trim()) throw new Error("Nome é obrigatório");
      if (!form.cpf || !form.cpf.trim()) throw new Error("CPF é obrigatório");

      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error("Sessão expirada.");

      // Upload avatar se houver
      let avatarUrl = form.avatar_url || null;
      if (avatarFile) {
        const fileExt = avatarFile.name.split(".").pop();
        const fileName = `${user.id}/${user.id}-${Date.now()}.${fileExt}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, avatarFile, { upsert: true });

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
        avatarUrl = data.publicUrl;
      }

      // 1) Tenta com conjunto A (M/F/O/N)
      const sexoA = normalizeSexToA(form.sexo || null);
      // Payload SEM owner_id (UPDATE não pode modificar owner_id)
      const updatePayload = {
        full_name: form.full_name.trim(),
        cpf: form.cpf.trim(),
        email: form.email?.trim() || null,
        celular: form.celular?.trim() || null,
        data_nascimento: form.data_nascimento || null,
        sexo: sexoA,
        avatar_url: avatarUrl
      };

      let error: any = null;

      if (id) {
        const result = await supabase.from("patients_onboarding").update(updatePayload).eq("id", id);
        error = result.error;
      }

      // 2) Fallback: se o banco usa pt (masculino/feminino/outro/nao_informado)
      if (isSexConstraintError(error)) {
        const sexoB = normalizeSexToB(form.sexo || null);
        const updatePayloadB = { ...updatePayload, sexo: sexoB };

        if (id) {
          const retry = await supabase.from("patients_onboarding").update(updatePayloadB).eq("id", id);
          if (retry.error) throw new Error(retry.error.message);
        }
      } else if (error) {
        throw new Error(error.message);
      }

      toast.success("Dados salvos com sucesso");
      navigate(patientDetailsPath);
    } catch (e: any) {
      setErr(e.message || "Falha ao salvar paciente");
      toast.error(e.message || "Falha ao salvar paciente");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(patientDetailsPath)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isPatientPortal ? "Editar Perfil" : "Editar Paciente"}
            </h1>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(patientDetailsPath)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isPatientPortal ? "Editar Perfil" : "Editar Paciente"}
          </h1>
          <p className="text-muted-foreground">
            {isPatientPortal
              ? "Atualize as informações abaixo"
              : "Atualize as informações do paciente abaixo."}
          </p>
        </div>
      </div>

      <Card>
        {!isPatientPortal && (
          <CardHeader>
            <CardTitle>Informações do Paciente</CardTitle>
            <CardDescription>
              Preencha os campos abaixo para atualizar as informações do paciente.
            </CardDescription>
          </CardHeader>
        )}
        <CardContent className={cn("space-y-6", isPatientPortal && "pt-6")}>
          {/* Avatar upload */}
          <div className="flex justify-center">
            <div className="relative">
              {avatarPreview ? (
                <div className="relative">
                  <Avatar className="w-24 h-24 border-2 border-primary">
                    <AvatarImage src={avatarPreview} alt="Avatar" />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      <User className="h-8 w-8" />
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={removeAvatar}
                    className="absolute top-0 right-0 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <label className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center cursor-pointer hover:border-primary transition">
                  <User className="h-8 w-8 text-muted-foreground" />
                  <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Nome completo */}
            <div className="space-y-2">
              <Label htmlFor="full_name">
                Nome completo <span className="text-destructive">*</span>
              </Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                placeholder="Nome completo do paciente"
              />
            </div>

            {/* CPF */}
            <div className="space-y-2">
              <Label htmlFor="cpf">
                CPF <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cpf"
                value={form.cpf}
                onChange={e => setForm(f => ({ ...f, cpf: e.target.value }))}
                placeholder="000.000.000-00"
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="email@exemplo.com"
              />
            </div>

            {/* Celular */}
            <div className="space-y-2">
              <Label htmlFor="celular">Celular</Label>
              <Input
                id="celular"
                value={form.celular}
                onChange={e => setForm(f => ({ ...f, celular: e.target.value }))}
                placeholder="(00) 00000-0000"
              />
            </div>

            {/* Data de nascimento */}
            <div className="space-y-2">
              <Label htmlFor="data_nascimento">Data de nascimento</Label>
              <Input
                id="data_nascimento"
                type="date"
                value={form.data_nascimento}
                onChange={e => setForm(f => ({ ...f, data_nascimento: e.target.value }))}
              />
            </div>

            {/* Sexo */}
            <div className="space-y-2">
              <Label htmlFor="sexo">Sexo</Label>
              <Select
                value={form.sexo || ""}
                onValueChange={(value) => setForm(f => ({ ...f, sexo: value }))}
              >
                <SelectTrigger id="sexo">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="F">Feminino</SelectItem>
                  <SelectItem value="O">Outro</SelectItem>
                  <SelectItem value="N">Não informado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {err && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {err}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(patientDetailsPath)}
              className="flex-1"
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="flex-1"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {busy ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

